import React, { useMemo, useCallback } from 'react'
import { useDrop } from 'react-dnd'
import { DraggableUserChip, ItemType } from './DraggableUserChip'
import styled from '@emotion/styled'
import { AttendeeData } from './ThreeSectionAttendeeManager/attendeeUtils'
import { DragItem } from './DraggableChipContainer'

interface Props {
    attendee: AttendeeData
    index: number
    onMove: (dragIndex: number, hoverIndex: number) => void
    onCrossSectionMove?: (
        attendee: AttendeeData,
        targetSection: string,
        dropPosition?: number
    ) => void
    onRemove?: (index: number) => void
    targetSection?: string
    icon?: React.ReactNode
    interactiveIcon?: React.ReactNode
    secondaryInteractiveIcon?: React.ReactNode
    totalChips?: number
    onMoveToNextSection?: (attendee: AttendeeData) => void
    onMoveToPreviousSection?: (attendee: AttendeeData) => void
    isTimeLate?: boolean
    isLocationRemote?: boolean
    onTimeIconToggle?: () => void
    onLocationIconToggle?: () => void
    onFocusInput?: () => void
    readonlyForCreate?: boolean
}

const SlotContainer = styled.div<{ isOver: boolean; isCrossSection?: boolean }>`
    position: relative;
    background-color: transparent;
    border: 2px dashed transparent;
    border-radius: 4px;
    min-height: 32px;
    min-width: auto;
    transition: all 0.2s ease;
    display: inline-block;
    margin: 4px 4px 0 10px;
    overflow: visible;
`

export const DroppableChipSlot: React.FC<Props> = ({
    attendee,
    index,
    onMove,
    onCrossSectionMove,
    onRemove,
    targetSection,
    icon,
    interactiveIcon,
    secondaryInteractiveIcon,
    totalChips,
    onMoveToNextSection,
    onMoveToPreviousSection,
    isTimeLate,
    isLocationRemote,
    onTimeIconToggle,
    onLocationIconToggle,
    onFocusInput,
    readonlyForCreate = false
}) => {
    const createAttendeeData = useCallback(
        (item: DragItem): AttendeeData => ({
            id: item.id || `fallback_${item.name || item.text || 'unknown'}`,
            external_id: item.external_id,
            text: item.name || item.text,
            name: item.name || item.text,
            email: item.email,
            isTimeLate: item.isTimeLate,
            isLocationRemote: item.isLocationRemote
        }),
        []
    )

    const handleDrop = useCallback(
        (item: DragItem, monitor) => {
            if (!monitor.didDrop()) {
                const dragIndex = item.index
                const hoverIndex = index
                const sourceSection = item.sourceSection

                if (sourceSection && targetSection && sourceSection !== targetSection) {
                    if (onCrossSectionMove) {
                        onCrossSectionMove(createAttendeeData(item), targetSection, hoverIndex)
                    }
                } else if (dragIndex !== hoverIndex) {
                    onMove(dragIndex, hoverIndex)
                }
            }
        },
        [index, targetSection, onCrossSectionMove, onMove, createAttendeeData]
    )

    const [{ isOver, canDrop, dropPosition }, drop] = useDrop<
        DragItem,
        void,
        { isOver: boolean; canDrop: boolean; dropPosition: 'left' | 'right' | null }
    >({
        accept: ItemType,
        drop: handleDrop,
        collect: (monitor) => {
            const isOverCurrent = monitor.isOver({ shallow: true })
            const dragItem = monitor.getItem()
            
            let position: 'left' | 'right' | null = null
            
            if (isOverCurrent && dragItem) {
                const sourceSection = dragItem.sourceSection
                const isCrossSectionDrag = sourceSection && targetSection && sourceSection !== targetSection
                
                if (isCrossSectionDrag) {
                    // For cross-section drags, item will be inserted at this position (left side)
                    position = 'left'
                } else {
                    // For same-section drags, determine position based on drag direction
                    const dragIndex = dragItem.index
                    // If dragging from left (lower index) to right (higher index), show indicator on right
                    // If dragging from right (higher index) to left (lower index), show indicator on left
                    if (dragIndex < index) {
                        position = 'right'
                    } else if (dragIndex > index) {
                        position = 'left'
                    }
                }
            }
            
            return {
                isOver: isOverCurrent,
                canDrop: monitor.canDrop(),
                dropPosition: position
            }
        }
    })

    const isCrossSection = useMemo(() => isOver && canDrop, [isOver, canDrop])

    const draggableUserChipProps = useMemo(
        () => ({
            id: attendee.id,
            name: attendee.text || attendee.name || '',
            email: attendee.email || null,
            index,
            sourceSection: targetSection,
            onMove,
            onRemove,
            icon,
            interactiveIcon,
            secondaryInteractiveIcon,
            totalChips,
            onMoveToNextSection,
            onMoveToPreviousSection,
            attendee,
            isTimeLate,
            isLocationRemote,
            onTimeIconToggle,
            onLocationIconToggle,
            onFocusInput,
            readonlyForCreate,
            isOver,
            dropPosition
        }),
        [
            attendee,
            index,
            targetSection,
            onMove,
            onRemove,
            icon,
            interactiveIcon,
            secondaryInteractiveIcon,
            totalChips,
            onMoveToNextSection,
            onMoveToPreviousSection,
            isTimeLate,
            isLocationRemote,
            onTimeIconToggle,
            onLocationIconToggle,
            onFocusInput,
            readonlyForCreate,
            isOver,
            dropPosition
        ]
    )

    return (
        <SlotContainer ref={drop} isOver={isOver} isCrossSection={isCrossSection} data-chip-slot>
            <DraggableUserChip {...draggableUserChipProps} />
        </SlotContainer>
    )
}

export default DroppableChipSlot
